import { useEffect, useState, type FormEvent } from 'react';
import { validateSettingsPatch } from '@muddle/core';
import { api } from '../api';
import { useAsync } from '../hooks/useAsync';
import { SHORTCUTS, useTitle } from '../shortcuts';
import { applyTheme, type ThemePref } from '../theme';
import { SIGNED_OUT_EVENT } from '../events';
import { ErrorMessage, PageHeader, Skeleton, useToast } from '../components';
import { AiCard } from './AiCard';
import { ConnectionCard } from './ConnectionCard';
import { BackupCards } from './BackupCards';

type DilutionKey = 'dilutionShake' | 'dilutionStir' | 'dilutionBuild' | 'dilutionBlend';
const DILUTION: [DilutionKey, string][] = [
  ['dilutionShake', 'Shaken'],
  ['dilutionStir', 'Stirred'],
  ['dilutionBuild', 'Built'],
  ['dilutionBlend', 'Blended'],
];
const blankDilution = Object.fromEntries(DILUTION.map(([k]) => [k, ''])) as Record<
  DilutionKey,
  string
>;

export function SettingsPage() {
  const toast = useToast();
  useTitle('Settings');
  const [state, reload] = useAsync(api.getSettings);
  const [health] = useAsync(api.health);
  const [busy, setBusy] = useState(false);
  const [session] = useAsync(api.authSession);

  const setTheme = async (theme: ThemePref) => {
    applyTheme(theme); // instant; saved below so it follows your data
    setBusy(true);
    try {
      await api.updateSettings({ theme });
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.', { error: true });
    } finally {
      setBusy(false);
    }
  };

  const setUnit = async (defaultUnit: 'oz' | 'ml') => {
    setBusy(true);
    try {
      await api.updateSettings({ defaultUnit });
      toast('Default unit saved');
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.', { error: true });
    } finally {
      setBusy(false);
    }
  };

  const [dilution, setDilution] = useState<Record<DilutionKey, string>>(blankDilution);
  const [dilutionError, setDilutionError] = useState('');
  useEffect(() => {
    if (state.status === 'ready') {
      setDilution(
        Object.fromEntries(
          DILUTION.map(([k]) => [k, String(Math.round(state.data[k] * 1000) / 10)]),
        ) as Record<DilutionKey, string>,
      );
    }
  }, [state]);

  const saveDilution = async (e: FormEvent) => {
    e.preventDefault();
    const patch = Object.fromEntries(DILUTION.map(([k]) => [k, Number(dilution[k]) / 100]));
    const v = validateSettingsPatch(patch);
    if (!v.value || Object.values(dilution).some((x) => x.trim() === '')) {
      setDilutionError('Enter each dilution as a percentage between 0 and 100.');
      return;
    }
    setDilutionError('');
    setBusy(true);
    try {
      await api.updateSettings(v.value);
      toast('Dilution saved');
      reload();
    } catch (err) {
      setDilutionError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader kicker="Settings" title="Settings and backup" />
      {state.status === 'loading' && <Skeleton />}
      {state.status === 'error' && (
        <ErrorMessage message={`Couldn't load settings. ${state.message}`} onRetry={reload} />
      )}
      {state.status === 'ready' && (
        <section className="card" aria-labelledby="def-title">
          <h2 className="card-title" id="def-title">
            Defaults
          </h2>
          <p className="card-sub">Used when you add ingredients and read recipes.</p>
          <div className="list-row">
            <span id="unit-label">Default unit</span>
            <div
              className="tabs"
              role="group"
              aria-labelledby="unit-label"
              style={{ marginLeft: 'auto' }}
            >
              {(['oz', 'ml'] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  className="tab"
                  disabled={busy}
                  aria-pressed={state.data.defaultUnit === u}
                  onClick={() => void setUnit(u)}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div className="list-row">
            <span id="theme-label">Appearance</span>
            <div
              className="tabs"
              role="group"
              aria-labelledby="theme-label"
              style={{ marginLeft: 'auto' }}
            >
              {(
                [
                  ['system', 'System'],
                  ['light', 'Light'],
                  ['dark', 'Dark'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="tab"
                  disabled={busy}
                  aria-pressed={state.data.theme === value}
                  onClick={() => void setTheme(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="list-row">
            <span>Database schema</span>
            <span className="num">
              {health.status === 'ready' ? `v${health.data.schemaVersion}` : '…'}
            </span>
          </div>
        </section>
      )}
      {state.status === 'ready' && (
        <section className="card" aria-labelledby="dil-title">
          <h2 className="card-title" id="dil-title">
            Dilution
          </h2>
          <p className="card-sub">
            Water added by ice, as a percentage of the ingredient volume. Used for the ABV and
            volume estimates.
          </p>
          <form onSubmit={(e) => void saveDilution(e)} noValidate>
            <div className="row row-2">
              {DILUTION.map(([key, label]) => (
                <div key={key}>
                  <label htmlFor={`dil-${key}`}>{label} (%)</label>
                  <input
                    id={`dil-${key}`}
                    className="num"
                    inputMode="decimal"
                    value={dilution[key]}
                    onChange={(e) => setDilution({ ...dilution, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            {dilutionError && (
              <div className="msg err" role="alert">
                {dilutionError}
              </div>
            )}
            <div className="actions">
              <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
                {busy ? 'Saving…' : 'Save dilution'}
              </button>
            </div>
          </form>
        </section>
      )}
      <section className="card" aria-labelledby="keys-title">
        <h2 className="card-title" id="keys-title">
          Keyboard shortcuts
        </h2>
        <p className="card-sub">Use Cmd instead of Ctrl on a Mac.</p>
        {SHORTCUTS.map(([keys, what]) => (
          <div key={keys} className="list-row">
            <span>{what}</span>
            <kbd className="mu-kbd">{keys}</kbd>
          </div>
        ))}
      </section>
      {session.status === 'ready' && session.data.authMode === 'password' && (
        <section className="card" aria-labelledby="acct-title">
          <h2 className="card-title" id="acct-title">
            Account
          </h2>
          <p className="card-sub">You are signed in on this device.</p>
          <div className="actions">
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() =>
                void api.logout().finally(() => window.dispatchEvent(new Event(SIGNED_OUT_EVENT)))
              }
            >
              Sign out
            </button>
          </div>
        </section>
      )}
      <ConnectionCard />
      <AiCard />
      <BackupCards />
    </div>
  );
}
